// Outline geometry over a `d` string: one closed subpath in stage space. Anchors are the on-curve points in draw order; segment i runs from anchor i to anchor i + 1, wrapping.
import { bezierToPoints, stringToRawPath } from '~/lib/svg/paths'

const EPS = 1e-9

// `stringToRawPath`'s own tolerance: a `Z` within half a unit of the start draws no closing line.
const CLOSE_TOLERANCE = 0.5

// Squared stage units of control-point deviation before a cubic is split; ~0.5 px.
const FLATTEN_THRESHOLD = 0.25

const parse = d => (typeof d === 'string' && d ? stringToRawPath(d) : [])

/** A closed polygon as `M..L..Z`. */
export const cornersToPath = corners => `M${corners.map(([x, y]) => `${x},${y}`).join('L')}Z`

// A segment's last anchor is its first again when the path closes onto it; that one is not an anchor of its own.
const closesOnStart = (segment, x, y) =>
	(x === segment[0] && y === segment[1]) ||
	(segment.closed && Math.abs(x - segment[0]) <= CLOSE_TOLERANCE && Math.abs(y - segment[1]) <= CLOSE_TOLERANCE)

const segmentAnchors = segment => {
	const anchors = []
	for (let i = 0; i < segment.length; i += 6) anchors.push([segment[i], segment[i + 1]])
	if (anchors.length > 1) {
		const [x, y] = anchors[anchors.length - 1]
		if (closesOnStart(segment, x, y)) anchors.pop()
	}
	return anchors
}

/** The on-curve points of `d`'s first subpath, in draw order, without the closing repeat of the first. [] when `d` does not parse. */
export const anchorsOf = d => {
	const segment = parse(d)[0]
	return segment ? segmentAnchors(segment) : []
}

// Stage units a handle may sit off its chord and still draw a straight line: a parsed `L` comes back as a cubic with handles at its thirds.
const STRAIGHT_TOLERANCE = 1e-6

const lerp = (a, b, t) => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t]

const onChord = (p, a, b) => {
	const dx = b[0] - a[0]
	const dy = b[1] - a[1]
	const len2 = dx * dx + dy * dy
	if (len2 <= EPS) return Math.hypot(p[0] - a[0], p[1] - a[1]) <= STRAIGHT_TOLERANCE
	const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / len2
	return (
		t >= -EPS &&
		t <= 1 + EPS &&
		Math.abs(dx * (p[1] - a[1]) - dy * (p[0] - a[0])) / Math.sqrt(len2) <= STRAIGHT_TOLERANCE
	)
}

/** `d`'s first subpath as `{ anchors, handles }`: segment i runs anchor i → i + 1, wrapping, and `handles[i]` is its cubic's `[c1, c2]`, or null where it is straight (a line, or a cubic whose handles lie on its chord). null when `d` does not parse. */
export const outlineSegments = d => {
	const segment = parse(d)[0]
	if (!segment) return null
	const anchors = segmentAnchors(segment)
	const n = anchors.length
	const handles = []
	for (let i = 0; i < n; i++) {
		const k = 2 + 6 * i
		// An unclosed subpath, or one whose `Z` lands within tolerance, has no cubic for its closing segment: a straight line.
		if (k + 5 >= segment.length) {
			handles.push(null)
			continue
		}
		const c1 = [segment[k], segment[k + 1]]
		const c2 = [segment[k + 2], segment[k + 3]]
		const a = anchors[i]
		const b = anchors[(i + 1) % n]
		handles.push(onChord(c1, a, b) && onChord(c2, a, b) ? null : [c1, c2])
	}
	return { anchors, handles }
}

const pt = ([x, y]) => `${x},${y}`

/** `{ anchors, handles }` back to one closed subpath: `L` for a straight segment, `C` for a curved one, `Z` drawing the closing segment when it is straight. A polygon reads exactly as `cornersToPath` spells it. */
export const segmentsToPath = ({ anchors, handles }) => {
	const n = anchors.length
	let d = `M${pt(anchors[0])}`
	for (let i = 0; i < n; i++) {
		const h = handles[i]
		const to = anchors[(i + 1) % n]
		if (h) d += `C${pt(h[0])} ${pt(h[1])} ${pt(to)}`
		else if (i < n - 1) d += `L${pt(to)}`
	}
	return `${d}Z`
}

const cubicAt = (a, c1, c2, b, t) => {
	const s = 1 - t
	const w0 = s * s * s
	const w1 = 3 * s * s * t
	const w2 = 3 * s * t * t
	const w3 = t * t * t
	return [w0 * a[0] + w1 * c1[0] + w2 * c2[0] + w3 * b[0], w0 * a[1] + w1 * c1[1] + w2 * c2[1] + w3 * b[1]]
}

const dist2 = (p, q) => (p[0] - q[0]) ** 2 + (p[1] - q[1]) ** 2

// The parameter of the cubic's point nearest `p`: a coarse scan, then a ternary search around the best sample.
const nearestT = (a, c1, c2, b, p) => {
	const N = 32
	let best = 0
	let bestD = Number.POSITIVE_INFINITY
	for (let j = 0; j <= N; j++) {
		const dd = dist2(cubicAt(a, c1, c2, b, j / N), p)
		if (dd < bestD) {
			bestD = dd
			best = j
		}
	}
	let lo = Math.max(0, (best - 1) / N)
	let hi = Math.min(1, (best + 1) / N)
	for (let k = 0; k < 40; k++) {
		const m1 = lo + (hi - lo) / 3
		const m2 = hi - (hi - lo) / 3
		if (dist2(cubicAt(a, c1, c2, b, m1), p) < dist2(cubicAt(a, c1, c2, b, m2), p)) hi = m2
		else lo = m1
	}
	return (lo + hi) / 2
}

// Split the cubic a→b where it passes nearest `point` (de Casteljau), then carry the split's two inner handles with it onto `point`: the halves keep the curve's shape where `point` lies on it, and stay tangent-continuous where it does not.
const splitCubic = (a, c1, c2, b, point) => {
	const t = nearestT(a, c1, c2, b, point)
	const p01 = lerp(a, c1, t)
	const p12 = lerp(c1, c2, t)
	const p23 = lerp(c2, b, t)
	const p012 = lerp(p01, p12, t)
	const p123 = lerp(p12, p23, t)
	const s = lerp(p012, p123, t)
	const dx = point[0] - s[0]
	const dy = point[1] - s[1]
	return [
		[p01, [p012[0] + dx, p012[1] + dy]],
		[[p123[0] + dx, p123[1] + dy], p23],
	]
}

const inRange = (i, n) => Number.isInteger(i) && i >= 0 && i < n

/** `d` with `point` inserted as a new anchor splitting segment `at` (anchor `at` → `at + 1`). A straight segment splits into two lines; a curved one into two cubics that keep its shape. null for an `at` the outline does not have. Unvalidated: the caller checks `isSimpleOutline`. */
export const insertAnchor = (d, at, point) => {
	const o = outlineSegments(d)
	const n = o?.anchors.length ?? 0
	if (!inRange(at, n)) return null
	const { anchors, handles } = o
	const h = handles[at]
	const halves = h ? splitCubic(anchors[at], h[0], h[1], anchors[(at + 1) % n], point) : [null, null]
	anchors.splice(at + 1, 0, point)
	handles.splice(at, 1, ...halves)
	return segmentsToPath({ anchors, handles })
}

/** `d` without anchor `at`; its two segments merge into one from its neighbours. Straight both sides → a line; otherwise a cubic keeping the outer handles (a straight side contributes the new chord's third). null for an `at` the outline does not have. Unvalidated. */
export const removeAnchor = (d, at) => {
	const o = outlineSegments(d)
	const n = o?.anchors.length ?? 0
	if (!inRange(at, n) || n < 2) return null
	const { anchors, handles } = o
	const prev = (at - 1 + n) % n
	const a = anchors[prev]
	const b = anchors[(at + 1) % n]
	const into = handles[prev]
	const out = handles[at]
	handles[prev] = into || out ? [into ? into[0] : lerp(a, b, 1 / 3), out ? out[1] : lerp(a, b, 2 / 3)] : null
	handles.splice(at, 1)
	anchors.splice(at, 1)
	return segmentsToPath({ anchors, handles })
}

/** `d` with every anchor and handle through `fn([x, y]) → [x, y]`: a translate, a mirror. Anchor order holds. null when `d` does not parse. */
export const mapOutline = (d, fn) => {
	const o = outlineSegments(d)
	if (!o?.anchors.length) return null
	return segmentsToPath({
		anchors: o.anchors.map(fn),
		handles: o.handles.map(h => h && [fn(h[0]), fn(h[1])]),
	})
}

/** A segment index wrapped into the count: a reference outlives anchor edits, so it may point past the end. 0 for no count or a non-finite index. */
export const wrapSegment = (i, n) => (n && Number.isFinite(i) ? ((Math.trunc(i) % n) + n) % n : 0)

/** Segment i's chord, anchor i → anchor i + 1, with i wrapped into the count. null under two anchors. */
export const segmentChord = (anchors, i) => {
	const n = anchors?.length ?? 0
	if (n < 2) return null
	const k = wrapSegment(i, n)
	return [anchors[k], anchors[(k + 1) % n]]
}

/** The anchors cycled so segment i (wrapped) runs 0 → 1: a reference's `edge` is which segment is the u axis, so a dragged anchor carries it. Empty or missing anchors come back as given. */
export const orientedAnchors = (anchors, i) => {
	const n = anchors?.length ?? 0
	if (!n) return anchors
	const k = wrapSegment(i, n)
	return anchors.map((_, j) => anchors[(j + k) % n])
}

/** Degrees of segment i's chord, in [0, 360), y down; 0 under two anchors. */
export const segmentAngle = (anchors, i) => {
	const chord = segmentChord(anchors, i)
	if (!chord) return 0
	const [[x0, y0], [x1, y1]] = chord
	return ((Math.atan2(y1 - y0, x1 - x0) * 180) / Math.PI + 360) % 360
}

// The subpath flattened to a closed polygon, the closing repeat and consecutive duplicates dropped. The closing segment is drawn even without a `Z`: an outline is a fill.
const flatten = segment => {
	const pts = [[segment[0], segment[1]]]
	const push = (x, y) => {
		const [px, py] = pts[pts.length - 1]
		if (Math.abs(x - px) > EPS || Math.abs(y - py) > EPS) pts.push([x, y])
	}
	for (let i = 0; i + 7 < segment.length; i += 6) {
		const flat = bezierToPoints(...segment.slice(i, i + 8), FLATTEN_THRESHOLD)
		for (let j = 2; j < flat.length; j += 2) push(flat[j], flat[j + 1])
	}
	const [x0, y0] = pts[0]
	while (pts.length > 1) {
		const [x, y] = pts[pts.length - 1]
		if (Math.abs(x - x0) > EPS || Math.abs(y - y0) > EPS) break
		pts.pop()
	}
	return pts
}

const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0])

const onSegment = (p, a, b) =>
	Math.abs(cross(a, b, p)) <= EPS &&
	p[0] >= Math.min(a[0], b[0]) - EPS &&
	p[0] <= Math.max(a[0], b[0]) + EPS &&
	p[1] >= Math.min(a[1], b[1]) - EPS &&
	p[1] <= Math.max(a[1], b[1]) + EPS

// Touching counts: a point resting on another edge pinches the outline in two.
const segmentsMeet = (a, b, c, d) => {
	const d1 = cross(c, d, a)
	const d2 = cross(c, d, b)
	const d3 = cross(a, b, c)
	const d4 = cross(a, b, d)
	if (((d1 > EPS && d2 < -EPS) || (d1 < -EPS && d2 > EPS)) && ((d3 > EPS && d4 < -EPS) || (d3 < -EPS && d4 > EPS)))
		return true
	return onSegment(a, c, d) || onSegment(b, c, d) || onSegment(c, a, b) || onSegment(d, a, b)
}

/** A closed polygon's signed area (shoelace): positive when it winds clockwise on screen (y down). */
export const polygonArea = pts => {
	let a = 0
	for (let i = 0; i < pts.length; i++) {
		const [x0, y0] = pts[i]
		const [x1, y1] = pts[(i + 1) % pts.length]
		a += x0 * y1 - x1 * y0
	}
	return a / 2
}

// A simple polygon, either winding: no edge doubling back on the last, no two edges meeting except neighbours at their shared point.
const isSimplePolygon = pts => {
	const n = pts.length
	if (n < 3 || Math.abs(polygonArea(pts)) <= EPS) return false
	for (let i = 0; i < n; i++) {
		const a = pts[i]
		const b = pts[(i + 1) % n]
		const c = pts[(i + 2) % n]
		if (Math.abs(cross(a, b, c)) <= EPS && (b[0] - a[0]) * (c[0] - b[0]) + (b[1] - a[1]) * (c[1] - b[1]) < 0)
			return false
	}
	for (let i = 0; i < n; i++)
		for (let j = i + 2; j < n; j++) {
			if (i === 0 && j === n - 1) continue
			if (segmentsMeet(pts[i], pts[(i + 1) % n], pts[j], pts[(j + 1) % n])) return false
		}
	return true
}

/** The bounds of `d`'s first subpath as drawn (curves flattened, not their handles): `[minX, minY, maxX, maxY]`, or null when it does not parse. */
export const outlineBounds = d => {
	const segment = parse(d)[0]
	if (!segment) return null
	const pts = flatten(segment)
	const xs = pts.map(p => p[0])
	const ys = pts.map(p => p[1])
	return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)]
}

/** One subpath enclosing area without crossing or touching itself, curves judged on their flattening (~0.5 px). An open subpath counts as closed by a straight line. */
export const isSimpleOutline = d => {
	const raw = parse(d)
	return raw.length === 1 && isSimplePolygon(flatten(raw[0]))
}

// Segment geometry for the pickers: per-segment flattening at the same ~0.5 px, cached by `d`.

// One segment as a polyline from a to b: a straight one is its chord, a curved one flattened.
const flatSegment = (a, h, b) => {
	if (!h) return [a, b]
	const flat = bezierToPoints(a[0], a[1], h[0][0], h[0][1], h[1][0], h[1][1], b[0], b[1], FLATTEN_THRESHOLD)
	const pts = []
	for (let j = 0; j < flat.length; j += 2) pts.push([flat[j], flat[j + 1]])
	return pts
}

const areaCentroid = (poly, area) => {
	let cx = 0
	let cy = 0
	for (let i = 0; i < poly.length; i++) {
		const [x0, y0] = poly[i]
		const [x1, y1] = poly[(i + 1) % poly.length]
		const w = x0 * y1 - x1 * y0
		cx += (x0 + x1) * w
		cy += (y0 + y1) * w
	}
	if (Math.abs(area) > EPS) return [cx / (6 * area), cy / (6 * area)]
	// Degenerate: the mean of the points.
	return [poly.reduce((s, p) => s + p[0], 0) / poly.length, poly.reduce((s, p) => s + p[1], 0) / poly.length]
}

const GEOMETRY_CACHE_SIZE = 64
const geometryCache = new Map()

/**
 * `d`'s first subpath as the pickers need it, flattened once per `d`: `{ d, segmentD(i), flats, polygon, area, label }`.
 * `d` is the outline re-spelled closed, so a stroke draws the closing segment even where the stored `d` has no `Z`.
 * Segment i runs anchor i → i + 1, wrapping; `flats[i]` is its polyline (curves at ~0.5 px), `segmentD(i)` its own `d` (exact).
 * `polygon` is the whole outline flattened, `area` its absolute area, `label` its area centroid; `anchors` and `handles` as `outlineSegments` reads them.
 * Cached: treat as frozen. null when `d` holds no anchors.
 */
export const outlineGeometry = d => {
	if (typeof d !== 'string' || !d) return null
	if (geometryCache.has(d)) return geometryCache.get(d)
	const o = outlineSegments(d)
	let out = null
	if (o?.anchors.length) {
		const { anchors, handles } = o
		const n = anchors.length
		const flats = anchors.map((a, i) => flatSegment(a, handles[i], anchors[(i + 1) % n]))
		// Each polyline's last point is the next one's first.
		const polygon = flats.flatMap(f => f.slice(0, -1))
		const signed = polygonArea(polygon)
		const segmentD = i => {
			const k = wrapSegment(i, n)
			const h = handles[k]
			const b = anchors[(k + 1) % n]
			return `M${pt(anchors[k])}${h ? `C${pt(h[0])} ${pt(h[1])} ${pt(b)}` : `L${pt(b)}`}`
		}
		out = {
			anchors,
			handles,
			d: segmentsToPath(o),
			segmentD,
			flats,
			polygon,
			area: Math.abs(signed),
			label: areaCentroid(polygon, signed),
		}
	}
	if (geometryCache.size >= GEOMETRY_CACHE_SIZE) geometryCache.delete(geometryCache.keys().next().value)
	geometryCache.set(d, out)
	return out
}

const toSegment = (p, a, b) => {
	const vx = b[0] - a[0]
	const vy = b[1] - a[1]
	const len2 = vx * vx + vy * vy
	const t = len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2)) : 0
	const foot = [a[0] + vx * t, a[1] + vy * t]
	return { dist: Math.hypot(p[0] - foot[0], p[1] - foot[1]), foot }
}

/** The point of `geo` (an `outlineGeometry`) nearest `p` within `r`, measured on the curve (its flattening): `{ at, dist, foot }`, `at` the segment index and `foot` the point on it as drawn, or null. */
export const nearestOnOutline = (geo, p, r) => {
	let best = null
	geo?.flats.forEach((f, at) => {
		for (let j = 0; j + 1 < f.length; j++) {
			const { dist, foot } = toSegment(p, f[j], f[j + 1])
			if (dist < (best?.dist ?? r)) best = { at, dist, foot }
		}
	})
	return best
}

/** The index of the segment of `geo` (an `outlineGeometry`) nearest `p` within `r`, measured on the curve (its flattening), or null. */
export const segmentAt = (geo, p, r) => nearestOnOutline(geo, p, r)?.at ?? null

/** The parameter in [0, 1] of the point of segment `at` of `geo` (an `outlineGeometry`) nearest `p`: along its chord when straight, on the cubic when curved. null for a segment `geo` does not have. */
export const segmentParam = (geo, at, p) => {
	const n = geo?.anchors.length ?? 0
	if (!inRange(at, n)) return null
	const a = geo.anchors[at]
	const b = geo.anchors[(at + 1) % n]
	const h = geo.handles[at]
	if (h) return nearestT(a, h[0], h[1], b, p)
	const vx = b[0] - a[0]
	const vy = b[1] - a[1]
	const len2 = vx * vx + vy * vy
	return len2 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * vx + (p[1] - a[1]) * vy) / len2)) : 0
}

/** Even-odd: inside a closed polygon, either winding. */
export const pointInPolygon = ([x, y], c) => {
	let inside = false
	for (let i = 0, j = c.length - 1; i < c.length; j = i++) {
		const [xi, yi] = c[i]
		const [xj, yj] = c[j]
		if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside
	}
	return inside
}

/** Distance from `p` to the nearest edge of a closed polygon, by brute force over every edge: the reference a baked field is checked against. */
export const edgeDepth = (p, c) => {
	let d = Infinity
	for (let i = 0; i < c.length; i++) d = Math.min(d, toSegment(p, c[i], c[(i + 1) % c.length]).dist)
	return d
}
